// src/pages/Popia.jsx
import React from "react";
import LegalPageLayout from "./LegalPageLayout";
import LegalDocumentContent from "./LegalDocumentContent";

export default function PopiaPage() {
  const popiaText = `GWIN POPIA CONSENT NOTICE
Effective date: 1 May 2026
Platform: Gwin
Operator: Marion Rich Pty Ltd, registration number 2022 / 577834 / 07, trading as “Gwin”
Jurisdiction: Republic of South Africa
Contact email: admin@gwin.co.za

1. Purpose of This Notice
Gwin respects your privacy and is committed to processing your personal information lawfully, fairly, responsibly, and transparently.
This POPIA Consent Notice explains what personal information Gwin may collect, why we collect it, how we use it, when we may share it, and what choices you have.
By creating an account, using the Gwin platform, completing onboarding, participating in lessons, submitting business information, generating scores, joining a partner programme, or using future funding-readiness features, you agree that Gwin may process your personal information as described in this Notice and our Privacy Policy.
Where required by law, we will ask for your specific consent before processing or sharing certain information.

2. Important POPIA Statement
The Protection of Personal Information Act, 4 of 2013, known as POPIA, protects personal information in South Africa.
Under POPIA, Gwin must process personal information in a lawful and responsible way.
This means we must, where applicable:
tell you what information we collect;
explain why we collect it;
process only what is reasonably necessary;
use it for lawful and specific purposes;
protect it with appropriate security measures;
avoid sharing it unlawfully;
give you certain rights over your personal information.

3. Who Is Collecting Your Information?
Your information is collected by:
Marion Rich Pty Ltd t/a Gwin
Registration number: 2022 / 577834 / 07
Address: 22 Percy Road, Ottery, Cape Town
Email: admin@gwin.co.za
Website: www.gwin.co.za
Gwin is generally the responsible party for the personal information collected through the platform.
This means Gwin decides why and how your personal information is processed.

4. What Information We May Collect
Depending on how you use Gwin, we may collect and process the following information.

4.1 Personal and Contact Information
This may include:
name and surname;
email address;
mobile number;
username;
password or login credentials;
city, province, or region;
communication preferences.

4.2 Account and Platform Information
This may include:
account registration details;
login history;
device information;
IP address;
app activity;
feature usage;
support messages;
consent records.

4.3 Business Information
This may include:
business name;
trading name;
company registration number;
industry;
business size;
number of employees;
turnover range;
business stage;
business goals;
location;
compliance-related information you choose to provide;
funding goals;
growth goals.

4.4 Learning and Progress Information
This may include:
lessons completed;
quizzes completed;
quiz answers;
XP points;
streaks;
badges;
levels completed;
practical assignments;
template usage;
certificates;
progress through the Gwin journey.

4.5 Scoring and Readiness Information
This may include:
knowledge scores;
application scores;
behaviour scores;
financial capability indicators;
funding-readiness indicators;
progress indicators;
dashboard results;
recommended next actions;
readiness gaps.

4.6 Financial Behaviour and Business Activity Information
This may include information you voluntarily submit about:
cash flow routines;
budgeting activity;
debtor and creditor tracking;
pricing exercises;
margin reviews;
forecast completion;
funding pack completion;
document readiness;
financial goals;
business routines;
self-reported financial behaviour.

4.7 Documents You Upload
If enabled, this may include:
completed templates;
business documents;
registration documents;
financial worksheets;
funding-readiness documents;
compliance documents;
identity or verification documents, where required.
You should only upload information that is requested and that you are authorised to share.

4.8 Payment and Subscription Information
This may include:
subscription plan;
billing name;
billing email;
invoices;
receipts;
payment status;
cancellation status;
refund requests.
Gwin generally does not store full card details. Payment details may be processed by a secure third-party payment provider.

5. Why We Process Your Information
Gwin may process your personal information for the following purposes.

5.1 To Create and Manage Your Account
We use your information to:
create your Gwin account;
verify basic account details;
help you log in securely;
manage account preferences;
provide customer support;
protect your account.

5.2 To Provide the Gwin Platform
We use your information to:
deliver lessons;
track your progress;
provide quizzes;
unlock features;
issue badges or certificates;
provide templates;
save your activity;
provide dashboards;
personalise your learning experience.

5.3 To Generate Scores and Readiness Indicators
We use your activity, submissions, quiz results, self-reported information, and practical assignments to generate progress scores and readiness indicators.
These scores are intended to help you understand:
what you have learned;
what you have applied;
which financial habits you are building;
what you may need to improve;
how your funding-readiness journey is progressing.
Gwin scores are not credit scores, loan approvals, financial advice, tax advice, accounting advice, legal advice, or guarantees of funding.

5.4 To Improve the Platform
We may use information to:
improve lessons;
improve quizzes;
improve scoring logic;
fix bugs;
reduce user drop-off;
improve onboarding;
understand which features are useful;
improve templates and dashboards.

5.5 To Communicate With You
We may contact you about:
account activity;
lesson reminders;
support queries;
security alerts;
product updates;
payment notices;
legal notices;
programme notices;
marketing messages, where permitted.

5.6 To Process Payments and Subscriptions
If you use paid features, we process information to:
manage billing;
process subscriptions;
issue invoices or receipts;
manage cancellations;
handle refunds;
prevent payment abuse.

5.7 To Support Partner or Sponsored Programmes
If you access Gwin through a partner, sponsor, accountant, bookkeeper, coach, incubator, accelerator, ESD programme, employer, funder, or institution, we may process relevant information to:
enrol you in the programme;
track participation;
confirm completion;
provide programme support;
report on programme outcomes;
share permitted progress data with the programme partner.
We will explain what information may be shared during programme onboarding.

5.8 To Protect Gwin and Other Users
We may process information to:
prevent fraud;
detect false submissions;
detect account sharing;
prevent score manipulation;
protect platform security;
enforce our Terms of Use;
investigate misuse;
protect Gwin’s intellectual property;
comply with legal obligations.

6. Specific Consent Statements
You may be asked to accept one or more of the following consent statements.
These can be used as separate checkboxes in the app.

6.1 Core Platform Consent
Checkbox wording:
I consent to Gwin collecting and processing my personal, account, business, learning, progress, and platform activity information to create my account, provide the platform, track my learning progress, generate scores and readiness indicators, personalise my experience, provide support, protect the platform, and improve Gwin, in accordance with the Privacy Policy and this POPIA Consent Notice.

6.2 Scoring and Readiness Consent
Checkbox wording:
I consent to Gwin using my learning activity, quiz results, practical assignments, self-reported business information, template usage, and platform activity to generate progress scores, capability indicators, behaviour indicators, and funding-readiness indicators.
Supporting explanation:
I understand that Gwin scores are for educational and readiness-support purposes only. They are not credit scores, loan approvals, legal certifications, tax clearances, accounting opinions, financial advice, or guarantees of funding.

6.3 Communication Consent
Checkbox wording:
I consent to Gwin contacting me by email, in-app message, WhatsApp, SMS, or phone for account notices, lesson reminders, support, platform updates, payment notices, security alerts, and programme-related communication.

6.4 Marketing Consent
Checkbox wording:
I agree to receive educational resources, product updates, offers, event invitations, partner opportunities, and marketing communication from Gwin. I understand that I can unsubscribe or opt out at any time.

6.5 Partner Programme Consent
Applicable when the user joins through an accountant, coach, incubator, ESD programme, sponsor, or institutional partner.
Checkbox wording:
I consent to Gwin sharing my selected registration, participation, progress, completion, and programme-related information with partners for the purpose of administering the programme, tracking participation, confirming completion, supporting my progress, and reporting on programme outcomes.

6.6 Advisor Dashboard Consent
Applicable if an accountant, bookkeeper, coach, or consultant will view a client’s progress.
Checkbox wording:
I consent to Gwin sharing my selected progress information, learning status, completion data, dashboard indicators, and agreed support information with my advisor so that they can support, guide, and monitor my financial capability journey.
I understand that detailed scores, financial submissions, uploaded documents, and funding-readiness information will only be shared with my advisor if I specifically agree to that level of sharing.

6.7 Funder Referral Consent
Use this later, only when Gwin starts introducing users to funders or funding partners.
Checkbox wording:
I consent to Gwin sharing my selected business information, funding-readiness information, supporting documents, and contact details with funding partners for the purpose of assessing potential funding options or funding-related opportunities.
I understand that Gwin does not guarantee funding, loan approval, investment, grants, credit terms, or any financial outcome. Any funding decision is made by the relevant funder or financial institution.

6.8 Soft Credit Check Consent
Use this only if Gwin later introduces soft credit check features and after legal review.
Checkbox wording:
I consent to Gwin and/or its authorised verification or credit information partners conducting a soft credit or credit-readiness check using the information I provide, for the purpose of assessing funding readiness or matching me with potential funding options.
I understand that this check may involve sharing my information with authorised third-party providers, such as credit bureaus, verification providers, funders, or data partners. I understand that Gwin will provide further details before any credit-related check is performed.

6.9 Accounting Software or Bank Data Consent
Checkbox wording:
I consent to Gwin connecting to my selected accounting software, bank data provider, or financial data provider for the purpose of importing, analysing, and displaying selected business financial information to support my dashboard, scoring, readiness indicators, and recommended next actions.
I understand that I will be shown what information is accessed, why it is accessed, and how it will be used before the connection is activated.

7. When We May Share Your Information
Gwin may share personal information only where lawful and necessary.
We may share information with:
hosting providers;
payment providers;
analytics providers;
email, SMS, or WhatsApp providers;
customer support tools;
software developers and technical providers;
professional advisers;
programme partners, where applicable;
advisors, where you consent;
funders, where you consent;
verification providers, where you consent;
regulators, courts, or authorities where legally required.
We do not sell your personal information.

8. Aggregated and Anonymised Information
Gwin may use aggregated or anonymised information for:
product improvement;
impact reporting;
partner reporting;
investor reporting;
industry insights;
platform analytics;
research;
public reports.
Aggregated or anonymised information should not reasonably identify you personally.
Examples include:
percentage of users completing a module;
average improvement in quiz scores;
common funding-readiness gaps;
cohort-level progress;
industry-level behaviour trends;
province-level engagement data.

9. Your Rights Under POPIA
Subject to POPIA and applicable law, you may have the right to:
ask whether Gwin holds personal information about you;
request access to your personal information;
request correction of inaccurate or outdated information;
request deletion of personal information where appropriate;
object to certain processing;
withdraw consent where processing is based on consent;
opt out of direct marketing;
complain to the Information Regulator;
request details of third parties who have received your information, where applicable.
To exercise these rights, contact us at:
Email: admin@gwin.co.za
We may need to verify your identity before processing your request.

10. Withdrawal of Consent
You may withdraw consent where processing is based on consent.
To withdraw consent, you may:
update your preferences in the app, if available;
click unsubscribe for marketing communication;
contact Gwin at admin@gwin.co.za
Withdrawing consent may affect your ability to use some features.
For example:
if you withdraw core platform consent, we may not be able to provide your account;
if you withdraw scoring consent, we may not be able to generate readiness indicators;
if you withdraw partner sharing consent, the partner may not be able to track your programme progress;
if you withdraw funder sharing consent, Gwin may not be able to refer you to funding partners.
Withdrawal of consent does not automatically affect processing that was already lawfully completed before withdrawal.

11. Security Measures
Gwin takes reasonable technical and organisational measures to protect personal information.
These may include:
secure hosting;
password protection;
access controls;
role-based permissions;
encryption where appropriate;
audit logs;
data backups;
confidentiality obligations for staff and contractors;
platform monitoring;
incident response procedures.
You are responsible for keeping your login details confidential.

12. Retention of Personal Information
Gwin will keep your personal information only for as long as reasonably necessary for the purposes described in this Notice and our Privacy Policy, unless a longer period is required or permitted by law.
We may retain information for:
account management;
service delivery;
payment records;
legal compliance;
dispute resolution;
security;
fraud prevention;
reporting obligations;
legitimate business records.
When information is no longer needed, we will delete, destroy, de-identify, or anonymise it where reasonably possible.

13. Cross-Border Processing
Some Gwin service providers may store or process information outside South Africa.
Where this happens, Gwin will take reasonable steps to ensure that appropriate privacy and security safeguards are in place, as required by POPIA.

14. Important Funding and Advice Disclaimer
You understand and agree that:
Gwin is not a bank, lender, credit provider, tax practitioner, accountant, attorney, investment adviser, debt counsellor, or financial adviser.
Gwin provides general business finance education and readiness support.
Gwin does not provide personalised financial, legal, tax, accounting, credit, lending, or investment advice.
Gwin does not guarantee funding.
Gwin scores are not credit scores or loan approvals.
A funder may reject your application even if you use Gwin or achieve a high score.
You remain responsible for your own business, financial, legal, tax, and funding decisions.

15. Contact and Complaints
For privacy questions, access requests, correction requests, deletion requests, objections, consent withdrawals, or complaints, contact:
Marion Rich Pty Ltd
Information Officer / Privacy Contact: Candice Shunmoogam
Email: admin@gwin.co.za
Address: 22 Percy Road, Ottery, Cape Town
Website: www.gwin.co.za
You may also lodge a complaint with the Information Regulator South Africa if you believe your personal information has been processed unlawfully.`;

  return (
    <LegalPageLayout title="POPIA" backTo="/contact" backLabel="Back">
      <LegalDocumentContent documentText={popiaText} />
    </LegalPageLayout>
  );
}